import { useCurrentUser } from 'hooks/useCurrentUser'
import { useGroup } from 'hooks/useGroup'
import { useQuery } from 'hooks/useQuery'

import { createProgramRoom, fetchUpspaceRoom } from 'utils/queries'

import { useClientSubscriptions } from './useClientSubscriptions'
import { useDMChatRooms } from './useDMChatRooms'

/**
 * @returns The amount of unread messages for a user across all possible chat rooms.
 */
export const useUnreadMessageCount = () => {
  const { data: currentUser } = useCurrentUser()
  const { data: subscriptions } = useClientSubscriptions({
    userId: currentUser?.id,
  })
  const { sortedChatRooms } = useDMChatRooms()

  const subscribedPrograms = subscriptions?.map((sub) => sub.program) ?? []

  const {
    meta,
    data: userGroup,
    isLoading: isUserProgramLoading,
  } = useGroup({ userId: currentUser?.id })
  const { hasSetupGroup } = meta
  const addUserProgram = !isUserProgramLoading && userGroup && hasSetupGroup
  let programs = subscribedPrograms
  if (currentUser && addUserProgram) {
    programs = [userGroup, ...subscribedPrograms]
  }

  return useQuery<number>(
    ['unreadMessageCount'],
    () => {
      let newUnreadMessageCount = 0
      sortedChatRooms.forEach((room) => {
        if (room.meta.newMessageCount !== undefined && room.meta.newMessageCount > 0) {
          newUnreadMessageCount += room.meta.newMessageCount
        }
      })

      const upspaceChatRoomIds = programs
        ? programs.map((program) => createProgramRoom({ programId: program.id }))
        : []

      Promise.all(upspaceChatRoomIds).then((roomIds) => {
        const upspaceChatRooms = roomIds.map((roomId) => {
          return fetchUpspaceRoom({ roomId: roomId.id, userId: currentUser?.id as string })
        })

        Promise.all(upspaceChatRooms).then((rooms) => {
          rooms.forEach((room) => {
            if (room && room.meta.newMessageCount !== undefined && room.meta.newMessageCount > 0) {
              newUnreadMessageCount += room.meta.newMessageCount
            }
          })
        })
      })

      return newUnreadMessageCount
    },
    {
      enabled: !!currentUser?.id,
      refetchInterval: 60000, // Refetches every minute.
    },
  )
}
